<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { initials } from '@corp/helpers/string'
import { faUser } from '@fortawesome/pro-solid-svg-icons'
import { ACCENT_COLORS, DEFAULT_BACKGROUND_COLOR } from '@consumer/constants/contactPhoto'

// Non-destructured defineProps — Storybook's vue-component-meta docgen
// drops object-typed args (`user`) on the docs page when defineProps is
// destructured with reactivity-transform. Keep props on `props.x` so the
// docs canvas renders with the right args.
const props = withDefaults(
  defineProps<{
    size?: number
    user?: { name?: string; profilePictureUrl?: string; backgroundColor?: string }
    withTooltip?: boolean
    circle?: boolean
    newLayout?: boolean
    useFullNameForInitials?: boolean
    hoverable?: boolean
    // When provided, the avatar renders as an <a> link with the hover treatment
    // applied automatically. Pass ariaLabel too so the link is meaningful to
    // assistive tech.
    href?: string
    ariaLabel?: string
  }>(),
  {
    size: 28,
    circle: true,
    hoverable: false,
  },
)

const userInitials = _computed(() => initials(props.user?.name))

const imageLoaded = _ref(false)

const imageSrc = _computed(() =>
  props.user?.profilePictureUrl?.includes('gravatar')
    ? `${props.user?.profilePictureUrl}?size=${props.size * 2}&d=404`
    : props.user?.profilePictureUrl)

const hasImage = _computed(() => !!props.user?.profilePictureUrl)

const bgColor = _computed(() => props.user?.backgroundColor || DEFAULT_BACKGROUND_COLOR)
const accentColor = _computed(() => ACCENT_COLORS[bgColor.value] || ACCENT_COLORS[DEFAULT_BACKGROUND_COLOR])

const showHover = _computed(() => props.hoverable || !!props.href)
</script>

<template>
  <component
    :is="props.href ? 'a' : 'div'"
    v-tooltip="props.withTooltip && props.user?.name"
    :href="props.href || undefined"
    :aria-label="props.href ? props.ariaLabel : undefined"
    class="profile-picture hide-in-percy shrink-0 relative items-center justify-center
      overflow-hidden font-bold tracking-tighter"
    :class="[
      props.withTooltip ? 'flex' : 'inline-flex',
      props.circle ? 'rounded-full' : 'rounded',
      { 'profile-picture--hoverable': showHover },
    ]"
    :style="`
      background-color: ${bgColor};
      color: ${accentColor};
      font-size: ${props.size / 2}px;
      height: ${props.size}px;
      width: ${props.size}px;
    `"
  >
    <GIcon
      v-if="(!imageLoaded || !hasImage) && !props.user?.name?.trim()"
      :icon="faUser"
      data-testid="profile-picture-icon"
    />
    <span
      v-else-if="!imageLoaded || !hasImage"
      class="uppercase"
      data-testid="profile-picture-initials"
    >
      {{ props.useFullNameForInitials ? props.user?.name : userInitials }}
    </span>
    <img
      v-show="imageLoaded && hasImage"
      :key="props.user?.profilePictureUrl"
      :src="imageSrc"
      alt=""
      :height="props.size"
      :width="props.size"
      class="object-cover"
      @load="imageLoaded = true"
      @error="imageLoaded = false"
    >
  </component>
</template>

<style scoped>
.profile-picture--hoverable {
  cursor: pointer;
}

.profile-picture--hoverable::after {
  background: transparent;
  content: "";
  inset: 0;
  pointer-events: none;
  position: absolute;
  transition: background 0.15s;
}

.profile-picture--hoverable:hover::after {
  background: rgb(0 0 0 / 10%);
}
</style>
